import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { QData } from "../../../types/basic-type-list";
import { IDeleteDataResponseAndPayload, TResponse, TResponseWithQData } from "../../../types/common";
import { IJob, IJobCreatePayload, IJobUpdatePayload } from "../../../types/api/job";
import {
  listJob,
  jobDetails,
  createJob,
  updateJob,
  deleteJob,
  saveJob,
} from "./callbacks";

export const useJobList = (
  qData: QData,
  options?: Omit<UseQueryOptions<TResponseWithQData<IJob[]>, Error>, "queryKey" | "queryFn">
): UseQueryResult<TResponseWithQData<IJob[]>, Error> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });

  const queryResult = useQuery<TResponseWithQData<IJob[]>, Error>({
    queryKey: [API_ENDPOINTS.JOB_LIST, { ...queryParams, total: null }],
    queryFn: async () => {
      const res = await listJob(queryParams);
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: res.data.map((x) => ({ key: x.id, ...x })),
      qData: { ...queryParams, total: res.extra?.total },
    }),
    initialData: {
      data: [],
      success: true,
      message: "",
      code: 200,
      qData: { page: 1, limit: 20 },
      extra: { page: 1, limit: 20, total: 0 },
    },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: (params: Partial<QData>) => {
      setQueryParams(params as QData);
    },
  };
};

export const useJobDetails = (
  id: string | number,
  options?: Omit<UseQueryOptions<TResponse<IJob>, Error>, "queryKey" | "queryFn">
): UseQueryResult<TResponse<IJob>, Error> => {
  return useQuery<TResponse<IJob>, Error>({
    queryKey: [API_ENDPOINTS.JOB_DETAILS, id],
    queryFn: async () => {
      const res = await jobDetails(id);
      return res.data;
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 300000,
    ...options,
  });
};

export const useJobCreate = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IJob>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<TResponse<IJob>, unknown, IJobCreatePayload> = {}
): UseMutationResult<TResponse<IJob>, unknown, IJobCreatePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IJob>, unknown, IJobCreatePayload>({
    mutationFn: async (data: IJobCreatePayload) => {
      const res = await createJob(data);
      return res.data;
    },
    onSuccess: (res: TResponse<IJob>, ...rest) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.JOB_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return;
          return {
            ...old,
            data: [res.data, ...old.data],
            extra: { ...old.extra, total: (old.extra?.total ?? 0) + 1 },
          };
        }
      );
      queryClient.setQueryData([API_ENDPOINTS.JOB_DETAILS, res.data?.id], res);
      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

export const useJobUpdate = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IJob>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<TResponse<IJob>, unknown, IJobUpdatePayload> = {}
): UseMutationResult<TResponse<IJob>, unknown, IJobUpdatePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IJob>, unknown, IJobUpdatePayload>({
    mutationFn: async (data: IJobUpdatePayload) => {
      const res = await updateJob(data);
      return res.data;
    },
    onSuccess: (res: TResponse<IJob>, ...rest) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.JOB_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return;
          return {
            ...old,
            data: old.data.map((j: IJob) => (j.id === res.data?.id ? res.data : j)),
          };
        }
      );
      queryClient.setQueryData([API_ENDPOINTS.JOB_DETAILS, res.data?.id], res);
      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

export const useJobDelete = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IDeleteDataResponseAndPayload>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<IDeleteDataResponseAndPayload>,
    unknown,
    IDeleteDataResponseAndPayload
  > = {}
): UseMutationResult<
  TResponse<IDeleteDataResponseAndPayload>,
  unknown,
  IDeleteDataResponseAndPayload
> => {
  const queryClient = useQueryClient();

  return useMutation<
    TResponse<IDeleteDataResponseAndPayload>,
    unknown,
    IDeleteDataResponseAndPayload
  >({
    mutationFn: async (data: IDeleteDataResponseAndPayload) => {
      const res = await deleteJob(data);
      return res.data;
    },
    onSuccess: (res, vars, ...rest) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.JOB_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return;
          const deletedId = Number(vars.ids);
          return {
            ...old,
            data: old.data.filter((j: IJob) => j.id !== deletedId),
            extra: {
              ...old.extra,
              total: Math.max((old.extra?.total ?? 1) - 1, 0),
            },
          };
        }
      );
      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

// Legacy alias — kept so older pages that import useJobSave still compile
export const useJobSave = useJobCreate;

// ── Clock In / Out (re-exports from job-detail-hooks for convenience) ─────────
export {
  useJobClockIn,
  useJobClockOut,
  useJobNotes,
  useJobAddNote,
  useJobDeleteNote,
} from "./job-detail-hooks";
